import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Drawer } from '@material-ui/core';

import logo from '../../../../assets/images/LogoDrivee.jpg';

import { SidebarNav } from './components';

const hexToRgb = (input) => {
  input += '';
  input = input.replace('#', '');
  const hexRegex = /[0-9A-Fa-f]/g;
  if (!hexRegex.test(input) || (input.length !== 3 && input.length !== 6)) {
    throw new Error('input is not a valid hex color.');
  }
  if (input.length === 3) {
    const first = input[0];
    const second = input[1];
    const last = input[2];
    input = first + first + second + second + last + last;
  }
  input = input.toUpperCase(input);
  const first = input[0] + input[1];
  const second = input[2] + input[3];
  const last = input[4] + input[5];
  return `${parseInt(first, 16)}, ${parseInt(second, 16)}, ${parseInt(
    last,
    16
  )}`;
};

const blackColor = '#000';

const defaultFont = {
  fontFamily: '"PT Sans", "Roboto", "Helvetica", "Arial", sans-serif',
  fontWeight: '300',
  lineHeight: '1.5em'
};

const boxShadow = {
  boxShadow: `0 10px 5px -12px rgba(${hexToRgb(
    blackColor
  )}, 0.42), 0 4px 25px 0px rgba(${hexToRgb(
    blackColor
  )}, 0.12), 0 8px 10px -5px rgba(${hexToRgb(blackColor)}, 0.2)`
};

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.white,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: theme.spacing(2)
  },
  divider: {
    margin: theme.spacing(2, 0)
  },
  nav: {
    marginBottom: theme.spacing(2)
  },
  drawerPaper: {
    border: 'none',
    position: 'fixed',
    top: '0',
    bottom: '0',
    left: '0',
    zIndex: '1032',
    transitionProperty: 'top, bottom, width',
    transitionDuration: '.2s, .2s, .35s',
    transitionTimingFunction: 'linear, linear, ease',
    overflow: 'auto',
    '@media (max-width: 960px)': {
      display: 'none'
    },
    ...boxShadow,
    width: drawerWidth,
    '&:before,&:after': {
      position: 'absolute',
      zIndex: '3',
      width: '100%',
      height: '100%',
      content: '""',
      display: 'block',
      top: '0'
    }
  },
  logo: {
    padding: '15px 0px',
    margin: '0',
    textAlign: 'center',
    display: 'block',
    position: 'relative',
    zIndex: '4',
    '&:after': {
      content: '""',
      position: 'absolute',
      bottom: '0',
      height: '1px',
      right: '15px',
      width: 'calc(100% - 30px)',
      backgroundColor: 'hsla(0,0%,100%,.3)'
    },
    '& a': {
      color: '#08b2c7'
    }
  },
  logoNormal: {
    ...defaultFont,

    transition: 'all 300ms linear',
    display: 'block',
    opacity: '1',
    transform: 'translate3d(0px, 0, 0)',
    textTransform: 'uppercase',
    padding: '5px 0px',
    marginLeft: '1rem',
    fontSize: '18px',
    whiteSpace: 'nowrap',
    fontWeight: 'bold',
    lineHeight: '30px',
    overflow: 'hidden',
    '&,&:hover,&:focus': {
      color: '#87cdd6'
    }
  },
  img: {
    margin: '0 auto',
    width: '65%',
    verticalAlign: 'middle',
    border: '0'
  }
}));

const Sidebar = ({ className, variant, logoText, ...rest }) => {
  const classes = useStyles();

  const pages = [
    {
      title: 'Get informed',
      href: '/panel'
    },
    {
      title: 'Let\'s Analyze',
      subTitle1: 'Feedback',
      subTitle2: 'Financial Model',
      href1: '/newfeedback',
      href2: '/financialmodel'
    },
    {
      title: 'Invest or trade',
      href: '/invest'
    },
    {
      title: 'Interact with issuer',
      href: '/discuss'
    },
    {
      title: 'Logout',
      href: '/logout'
    }
  ]; 

  return (
    <div>
      <Drawer
        anchor="left"
        classes={{ paper: classes.drawerPaper }}
        open
        variant={variant}>
        <div className={classes.logo}>
          <a href="https://praexo.com/" rel="noopener noreferrer" target="_blank">
            <img alt="logo" className={classes.img} src={logo} />
          </a>
          <a
            className={classes.logoNormal}
            href="https://praexo.com/"
            rel="noopener noreferrer"
            target="_blank">
            {logoText}
          </a>
        </div>
        <div {...rest} className={clsx(classes.root, className)}>
          <SidebarNav className={classes.nav} pages={pages} />
        </div>
      </Drawer>
    </div>
  );
};

Sidebar.propTypes = {
  className: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  variant: PropTypes.string.isRequired,
  logoText: PropTypes.string.isRequired
};

export default Sidebar;
